
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, Loader2, Lightbulb, FileSpreadsheet, RotateCcw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface UseCaseIdentificationChallengeProps {
  userInitials: string;
  isChildComponent?: boolean;
}

interface ChatMessageData {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export const UseCaseIdentificationChallenge = ({ userInitials, isChildComponent = false }: UseCaseIdentificationChallengeProps) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessageData[]>([
    {
      role: 'assistant',
      content: "Today's challenge: Identify three specific AI use cases for improving customer service in your organization. For each use case, explain the business problem it solves and the expected ROI.",
      timestamp: new Date().toISOString()
    }
  ]);
  const [activeTab, setActiveTab] = useState('exercise');

  const handleNewChallenge = () => {
    setIsLoading(true);
    
    const challenges = [
      "Identify three AI use cases to optimize your supply chain operations. For each use case, describe the problem, solution, and implementation challenges.",
      "Identify AI use cases to enhance personalization in your marketing efforts. What specific outcomes would signal success?",
      "Identify AI use cases for improving operational efficiency in your HR department. Which would have the highest impact with the lowest implementation barrier?",
      "Identify three AI use cases for financial risk assessment in your organization. How would you measure the ROI for each?",
      "Identify specific use cases where generative AI could transform your product development process. What ethical considerations should be addressed?"
    ];
    
    const randomChallenge = challenges[Math.floor(Math.random() * challenges.length)];
    
    const newChallengeMessage: ChatMessageData = {
      role: 'assistant',
      content: `New challenge: ${randomChallenge}`,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages([newChallengeMessage]);
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      toast({
        title: "Empty submission",
        description: "Please identify use cases before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    // Add user message
    const userMessage: ChatMessageData = {
      role: 'user',
      content: prompt,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Get challenge description from the last assistant message
      // Instead of findLast, we'll use filter and then get the last item
      const assistantMessages = chatMessages.filter(msg => msg.role === 'assistant');
      const challengeDescription = assistantMessages.length > 0 
        ? assistantMessages[assistantMessages.length - 1].content 
        : '';
      
      const { data, error } = await supabase.functions.invoke('generate-practice-feedback', {
        body: {
          userPrompt: prompt,
          challengeDescription,
          previousMessages: chatMessages
        }
      });
      
      if (error) {
        throw new Error('Failed to get feedback.');
      }
      
      // Add assistant response
      const assistantMessage: ChatMessageData = {
        role: 'assistant',
        content: data.feedback,
        timestamp: new Date().toISOString()
      };
      
      setChatMessages((prev) => [...prev, assistantMessage]);
      setPrompt('');
    } catch (error) {
      console.error('Error submitting use cases:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit your use cases.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!isChildComponent && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Use Case Identification</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleNewChallenge}
            disabled={isLoading}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            New Challenge
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-grow flex flex-col">
        <TabsList className="w-full">
          <TabsTrigger value="exercise">Exercise</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>
        
        <TabsContent value="exercise" className="flex-grow flex flex-col space-y-4 overflow-auto p-1">
          <div className="flex-grow overflow-auto">
            {chatMessages.map((message, index) => (
              <div 
                key={index} 
                className={`mb-4 p-3 rounded-lg ${
                  message.role === 'assistant' 
                    ? 'bg-muted' 
                    : 'bg-primary/10 ml-4'
                }`}
              >
                <div className="flex items-center mb-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-medium ${
                    message.role === 'assistant' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background text-foreground border'
                  }`}>
                    {message.role === 'assistant' ? 'AI' : userInitials}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="pl-8 whitespace-pre-wrap">{message.content}</div>
              </div>
            ))}
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-col space-y-3 mt-auto">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Type your identified use cases here..."
              className="min-h-[100px] resize-none"
              disabled={isLoading}
            />
            <Button type="submit" disabled={isLoading || !prompt.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Use Cases
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </TabsContent>
        
        <TabsContent value="resources" className="flex-grow flex flex-col p-4 space-y-4">
          <Card className="p-4 border-l-4 border-l-purple-500">
            <h4 className="flex items-center font-medium mb-2">
              <Lightbulb className="h-4 w-4 mr-2 text-purple-500" />
              Identifying Good AI Use Cases
            </h4>
            <p className="text-sm text-muted-foreground">
              The best AI use cases typically address repetitive tasks, involve large datasets, 
              require predictive insights, or benefit from pattern recognition. Look for areas 
              where human judgement can be augmented rather than replaced.
            </p>
          </Card>
          
          <Card className="p-4 border-l-4 border-l-orange-500">
            <h4 className="flex items-center font-medium mb-2">
              <FileSpreadsheet className="h-4 w-4 mr-2 text-orange-500" />
              Use Case Evaluation Framework
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Business value and expected ROI</li>
              <li>• Data availability, quality, and accessibility</li>
              <li>• Technical feasibility and complexity</li>
              <li>• Implementation timeline and resources</li>
              <li>• Ethical considerations and risks</li>
              <li>• Integration with existing systems</li>
              <li>• Organizational readiness</li>
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
